import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Loader2,
  RefreshCcw,
  Send,
  Ticket,
  X,
} from 'lucide-react';
import { adminApi } from './adminApi';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';

/**
 * AdminSupportTicketsPanel — SuperAdmin triage datagrid for SupportTickets.
 *
 * Features:
 *   • Datagrid showing every ticket across every tenant, sorted
 *     breached-first (the backend already does this).
 *   • Live 6-hour SLA countdown — bold crimson when `is_sla_breached`.
 *   • Inline status dropdown (OPEN / WORK_IN_PROGRESS / CLOSED) that
 *     POSTs straight to PATCH /api/support/admin/tickets/:id/status.
 *   • Clicking a row opens a right-edge slide-in drawer with the full
 *     conversation timeline and an `admin_notes` composer.
 *   • Auto-refreshes the list every 30s + a manual refresh button.
 *
 * Note on real-time: WebSocket fan-out is on the backlog; for now the
 * auto-refresh keeps the grid fresh enough for human-paced triage.
 */
const STATUS_OPTIONS = [
  { value: 'OPEN', label: 'Requested (Open)', color: 'amber' },
  { value: 'WORK_IN_PROGRESS', label: 'Work-In-Progress', color: 'sky' },
  { value: 'CLOSED', label: 'Closed', color: 'emerald' },
];

function formatCountdown(seconds) {
  if (seconds == null) return '—';
  const breached = seconds < 0;
  const abs = Math.abs(seconds);
  const h = Math.floor(abs / 3600);
  const m = Math.floor((abs % 3600) / 60);
  const s = abs % 60;
  const pad = (n) => String(n).padStart(2, '0');
  const txt = `${pad(h)}:${pad(m)}:${pad(s)}`;
  return breached ? `-${txt}` : txt;
}

function formatLocalTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch (_) {
    return iso;
  }
}


export function AdminSupportTicketsPanel() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all'); // 'all' | 'open' | 'wip' | 'closed' | 'breached'
  const [selectedId, setSelectedId] = useState(null);
  const [draftNote, setDraftNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  // tick increments every second so the countdown labels re-render
  // without us having to refetch the list every second.
  const [tick, setTick] = useState(0);
  const pollRef = useRef(null);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter === 'open') params.set('status', 'OPEN');
      else if (filter === 'wip') params.set('status', 'WORK_IN_PROGRESS');
      else if (filter === 'closed') params.set('status', 'CLOSED');
      else if (filter === 'breached') params.set('breached_only', 'true');
      const r = await adminApi.get(`/support/admin/tickets?${params.toString()}`);
      setTickets(Array.isArray(r.data) ? r.data : []);
    } catch (_) {
      toast.error('Could not load tickets');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  // Initial + filter-change fetch.
  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  // Auto-poll every 30s; clear when the panel unmounts.
  useEffect(() => {
    pollRef.current = setInterval(fetchTickets, 30_000);
    return () => clearInterval(pollRef.current);
  }, [fetchTickets]);

  // Countdown ticker — 1 second granularity is plenty for a 6h SLA.
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const selectedTicket = useMemo(
    () => tickets.find((t) => t.id === selectedId) || null,
    [tickets, selectedId, tick], // include tick so derived countdown refreshes
  );

  const updateStatus = async (ticketId, newStatus) => {
    try {
      const r = await adminApi.patch(`/support/admin/tickets/${ticketId}/status`, { status: newStatus });
      toast.success(`Status → ${newStatus.replace('_', ' ')}`);
      setTickets((prev) => prev.map((t) => (t.id === ticketId ? r.data : t)));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Status update failed');
    }
  };

  const submitNote = async () => {
    if (!selectedTicket) return;
    const msg = draftNote.trim();
    if (!msg) return;
    setSavingNote(true);
    try {
      const r = await adminApi.post(`/support/admin/tickets/${selectedTicket.id}/note`, { message: msg });
      toast.success('Note posted to client workspace');
      setDraftNote('');
      setTickets((prev) => prev.map((t) => (t.id === selectedTicket.id ? r.data : t)));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Note save failed');
    } finally {
      setSavingNote(false);
    }
  };

  // Render-time countdown — use `tick` to force re-evaluation each second.
  // We rebase the remaining seconds against the original `created_at` so
  // we don't drift even when the polling is delayed by network latency.
  const computeRemaining = (t) => {
    if (!t || t.status === 'CLOSED' || !t.created_at) return null;
    const created = new Date(t.created_at).getTime();
    const deadline = created + 6 * 3600 * 1000;
    return Math.floor((deadline - Date.now()) / 1000);
  };

  return (
    <div data-testid="admin-tickets-panel" className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-white font-semibold flex items-center gap-2">
            <Ticket className="w-4 h-4 text-amber-400" />
            Support Ticket Center
          </h2>
          <p className="text-xs text-slate-400">6-hour SLA — breached tickets surface in bold crimson at the top.</p>
        </div>
        <div className="flex items-center gap-2">
          {STATUS_OPTIONS.length && [
            { v: 'all', label: 'All' },
            { v: 'breached', label: 'Breached' },
            { v: 'open', label: 'Open' },
            { v: 'wip', label: 'WIP' },
            { v: 'closed', label: 'Closed' },
          ].map((opt) => (
            <button
              key={opt.v}
              onClick={() => setFilter(opt.v)}
              data-testid={`admin-tickets-filter-${opt.v}`}
              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${filter === opt.v
                ? 'bg-amber-500 text-slate-900 border-amber-500 font-semibold'
                : 'bg-slate-900 text-slate-300 border-slate-700 hover:bg-slate-800'}`}
            >
              {opt.label}
            </button>
          ))}
          <Button
            variant="outline"
            onClick={fetchTickets}
            disabled={loading}
            data-testid="admin-tickets-refresh"
            className="h-8 px-2.5 border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800 hover:text-white"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCcw className="w-3.5 h-3.5" />}
          </Button>
        </div>
      </div>

      {/* Datagrid */}
      <div className="bg-slate-900/70 border border-slate-800 rounded-2xl overflow-hidden">
        <div className="grid grid-cols-[140px_minmax(220px,1fr)_200px_160px_180px] text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-800 bg-slate-950/60">
          <div className="px-3 py-2.5">Ticket ID</div>
          <div className="px-3 py-2.5">Subject & User</div>
          <div className="px-3 py-2.5">Created</div>
          <div className="px-3 py-2.5">SLA Countdown</div>
          <div className="px-3 py-2.5">Status</div>
        </div>
        {tickets.length === 0 && !loading && (
          <div className="px-6 py-12 text-center text-slate-500 text-sm">
            <Ticket className="w-8 h-8 mx-auto mb-2 text-slate-700" />
            No tickets in this view yet.
          </div>
        )}
        {tickets.map((t) => {
          const remaining = computeRemaining(t);
          const breached = remaining != null && remaining < 0;
          return (
            <div
              key={t.id}
              data-testid={`admin-ticket-row-${t.id}`}
              onClick={() => setSelectedId(t.id)}
              className={`grid grid-cols-[140px_minmax(220px,1fr)_200px_160px_180px] text-sm border-b border-slate-800/60 cursor-pointer transition-colors ${selectedId === t.id ? 'bg-amber-500/5' : 'hover:bg-slate-800/40'}`}
            >
              <div className="px-3 py-3 font-mono text-[11px] text-slate-400 truncate" title={t.id}>
                #{t.id.slice(0, 8)}
              </div>
              <div className="px-3 py-3 min-w-0">
                <div className="text-slate-100 font-medium truncate">{t.subject}</div>
                <div className="text-[11px] text-slate-500 truncate">
                  {t.user_name || t.user_email || t.user_id} · {t.user_email && t.user_name ? t.user_email : ''}
                </div>
              </div>
              <div className="px-3 py-3 text-[12px] text-slate-400">
                {formatLocalTime(t.created_at)}
              </div>
              <div
                className={`px-3 py-3 font-mono text-[13px] flex items-center gap-1.5 ${t.status === 'CLOSED'
                  ? 'text-slate-500'
                  : breached
                    ? 'text-red-400 font-bold animate-pulse'
                    : remaining != null && remaining < 1800
                      ? 'text-amber-400 font-semibold'
                      : 'text-emerald-400'}`}
                data-testid={`admin-ticket-sla-${t.id}`}
              >
                {breached ? <AlertTriangle className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
                {t.status === 'CLOSED' ? <span className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Closed</span> : formatCountdown(remaining)}
              </div>
              <div className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                <select
                  value={t.status}
                  onChange={(e) => updateStatus(t.id, e.target.value)}
                  data-testid={`admin-ticket-status-${t.id}`}
                  className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-xs text-slate-100 cursor-pointer hover:border-slate-600"
                >
                  {STATUS_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>
          );
        })}
      </div>

      {/* Slide-in side drawer.
          z-[60] (above the floating Emergent badge which sits at
          z-index:50 / bottom-right) so the footer 'Post note' button is
          always clickable. */}
      {selectedTicket && (
        <div
          className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm"
          onClick={() => setSelectedId(null)}
          data-testid="admin-ticket-drawer-overlay"
        >
          <aside
            data-testid="admin-ticket-drawer"
            onClick={(e) => e.stopPropagation()}
            className="absolute right-0 top-0 bottom-0 w-full sm:w-[520px] bg-slate-950 border-l border-slate-800 shadow-2xl flex flex-col z-[60]"
          >
            <header className="border-b border-slate-800 px-5 py-4 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-mono text-[11px] text-slate-500">#{selectedTicket.id}</div>
                <h3 className="text-white font-semibold mt-1 truncate">{selectedTicket.subject}</h3>
                <div className="text-[11px] text-slate-500 mt-1">
                  From {selectedTicket.user_name || selectedTicket.user_email} · created {formatLocalTime(selectedTicket.created_at)}
                </div>
              </div>
              <button
                onClick={() => setSelectedId(null)}
                data-testid="admin-ticket-drawer-close"
                className="text-slate-400 hover:text-white p-1.5 rounded hover:bg-slate-800"
              >
                <X className="w-4 h-4" />
              </button>
            </header>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">Original Request</div>
                <p className="text-slate-100 text-sm whitespace-pre-wrap">{selectedTicket.detailed_description}</p>
              </div>

              {/* Interleaved timeline */}
              {(() => {
                const timeline = [
                  ...(selectedTicket.user_responses || []).map((m) => ({ ...m, side: 'user' })),
                  ...(selectedTicket.admin_notes || []).map((m) => ({ ...m, side: 'admin' })),
                ].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
                if (!timeline.length) {
                  return (
                    <div className="text-center text-slate-500 text-xs py-4">
                      No replies yet — be the first to respond.
                    </div>
                  );
                }
                return timeline.map((m) => (
                  <div
                    key={m.id}
                    data-testid={`admin-ticket-msg-${m.side}`}
                    className={`rounded-xl p-3 border ${m.side === 'admin'
                      ? 'bg-amber-500/5 border-amber-500/30'
                      : 'bg-slate-900/60 border-slate-800'}`}
                  >
                    <div className="text-[10px] uppercase tracking-wider mb-1 flex items-center justify-between">
                      <span className={m.side === 'admin' ? 'text-amber-400' : 'text-sky-400'}>
                        {m.side === 'admin' ? 'SuperAdmin' : 'Client'} · {m.author_name}
                      </span>
                      <span className="text-slate-500 normal-case font-normal">{formatLocalTime(m.created_at)}</span>
                    </div>
                    <p className="text-sm text-slate-100 whitespace-pre-wrap">{m.message}</p>
                  </div>
                ));
              })()}
            </div>

            <div className="border-t border-slate-800 px-5 pt-4 pb-4 space-y-2 bg-slate-950 relative z-[61]">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Add admin note (visible to client)</div>
              <Textarea
                value={draftNote}
                onChange={(e) => setDraftNote(e.target.value)}
                rows={3}
                placeholder="Hi — we've identified the issue. Working on a fix, should be ready by EOD."
                className="bg-slate-900 border-slate-700 text-slate-100 placeholder:text-slate-500 text-sm"
                data-testid="admin-ticket-note-input"
              />
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <Button
                  onClick={submitNote}
                  disabled={savingNote || !draftNote.trim()}
                  data-testid="admin-ticket-note-submit"
                  className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold relative z-[62] order-1"
                >
                  {savingNote ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                  Post note
                </Button>
                <div className="text-[11px] text-slate-500 max-w-[60%] order-2 ml-auto pr-2">
                  Pushes to <code className="text-slate-400">admin_notes[]</code> &amp; surfaces on the client&apos;s ticket view.
                </div>
              </div>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}

export default AdminSupportTicketsPanel;
